import { useSubmitEntity } from './useSubmitEntity';
import { useSnackbar } from './useSnackbar';
import { validateRequiredToSubmit, FieldValidation } from '@/lib/utils/submitValidation';
import { log } from '@/lib/utils/logger';
import { TreeNode } from '@/types/tree';

/**
 * Generic hook for submitting entities with client-side validation
 * Validates required-to-submit fields before attempting backend submit
 */
export const useSubmitWithValidation = () => {
  const { submitEntity } = useSubmitEntity();
  const { showSnackbar } = useSnackbar();

  /**
   * Submit entity with validation
   * Validates required-to-submit fields before submitting
   * @param node The tree node to submit
   * @param validationFields Array of field validations to check
   * @param entityLabel Label for the entity type (for error messages)
   * @returns Object with success status and field errors (if validation failed)
   */
  const submitWithValidation = async (
    node: TreeNode,
    validationFields: FieldValidation[],
    entityLabel: string = 'Entity'
  ): Promise<{ success: boolean; fieldErrors?: Record<string, string> }> => {
    const validationResult = validateRequiredToSubmit(validationFields);

    if (!validationResult.isValid) {
      log.warn(`[useSubmitWithValidation] ${entityLabel} validation failed:`, validationResult.errors);
      showSnackbar(
        `Cannot submit ${entityLabel.toLowerCase()}. Please complete the following fields: ${validationResult.errors.join(', ')}`,
        'warning',
        10000, // Auto-hide after 10 seconds
        'Validation Failed'
      );
      return { success: false, fieldErrors: validationResult.fieldErrors };
    }

    // All validation passed, proceed with submit
    const submitSuccess = await submitEntity(node);
    return { success: submitSuccess };
  };

  return {
    submitWithValidation,
  };
};
